import type { BaseQueryApi } from "@reduxjs/toolkit/query";
import {
	type BaseQueryFn,
	createApi,
	type FetchArgs,
	type FetchBaseQueryError,
	fetchBaseQuery,
} from "@reduxjs/toolkit/query/react";
import { getApiBaseUrl } from "../config/env";
import { API_ENDPOINTS } from "../constants/apiEndpoints";
import {
	logout,
	setCredentials,
	updateUser,
} from "../features/auth/auth.slice";
import {
	mergeSessionUser,
	parseLoginResponse,
} from "../features/auth/auth.utils";
import {
	extractApiErrorMessage,
	extractSpreadsheetUploadValidationErrors,
	FORCE_LOGOUT_MESSAGE_KEY,
	formatSpreadsheetUploadValidationToast,
	isForceLogoutAuthCode,
	isInsufficientPermissionCode,
	isSpreadsheetUploadValidationFailure,
	isTimestampOverwriteConflict,
	isTokenExpiredAuthCode,
	parseApiError,
} from "../features/auth/authApiErrors";
import { addToast } from "../features/toast/toast.slice";
import type { RootState } from "./store";

const apiBaseUrl = getApiBaseUrl();

function requestUrl(args: string | FetchArgs): string {
	return typeof args === "string" ? args : args.url;
}

const realBaseQuery = fetchBaseQuery({
	baseUrl: apiBaseUrl,
	credentials: "include",
	prepareHeaders: (headers, { getState }) => {
		const token = (getState() as RootState).auth.accessToken;
		if (token) {
			headers.set("authorization", `Bearer ${token}`);
		}
		return headers;
	},
});

let refreshPromise: Promise<{
	data?: unknown;
	error?: FetchBaseQueryError;
}> | null = null;

let meRefreshPromise: Promise<boolean> | null = null;
let logoutInFlight = false;
let lastAuthToastKey = "";
let lastAuthToastAt = 0;
let lastApiErrorToastKey = "";
let lastApiErrorToastAt = 0;
const API_ERROR_TOAST_DEDUP_MS = 4000;

function resetRefreshPromise() {
	refreshPromise = null;
}

function isRefreshEndpoint(args: string | FetchArgs): boolean {
	const url = requestUrl(args);
	return (
		url.includes(API_ENDPOINTS.auth.refresh) || url.includes("refresh-token")
	);
}

function isPublicAuthEndpoint(args: string | FetchArgs): boolean {
	const url = requestUrl(args);
	return (
		url.includes(API_ENDPOINTS.auth.login) ||
		url.includes(API_ENDPOINTS.auth.samlExchange) ||
		url.includes(API_ENDPOINTS.auth.logout) ||
		isRefreshEndpoint(args)
	);
}

function isTokenReauthError(
	status: unknown,
	code: string | null,
	message: string | null,
): boolean {
	if (status === 401 || isTokenExpiredAuthCode(code)) return true;
	const text = (message ?? "").toLowerCase();
	return /tokens?\s+refreshed|access token expired|token expired|jwt expired/.test(
		text,
	);
}

function dispatchApiErrorToastOnce(
	api: BaseQueryApi,
	payload: {
		type: "error";
		title?: string;
		message: string;
		lines?: Array<{ label?: string; text: string }>;
		duration?: number;
	},
) {
	const now = Date.now();
	const key = `${payload.title ?? ""}:${payload.message}:${(payload.lines ?? [])
		.map((line) => line.text)
		.join("|")}`;
	if (
		key === lastApiErrorToastKey &&
		now - lastApiErrorToastAt < API_ERROR_TOAST_DEDUP_MS
	) {
		return;
	}
	lastApiErrorToastKey = key;
	lastApiErrorToastAt = now;
	api.dispatch(addToast(payload));
}

function dispatchAuthToastOnce(
	api: BaseQueryApi,
	payload: { type: "success" | "error"; message: string },
) {
	if (/tokens?\s+refreshed/i.test(payload.message)) return;
	const now = Date.now();
	const key = `${payload.type}:${payload.message}`;
	if (key === lastAuthToastKey && now - lastAuthToastAt < 4000) {
		return;
	}
	lastAuthToastKey = key;
	lastAuthToastAt = now;
	api.dispatch(addToast(payload));
}

/**
 * Clears local session, best-effort server logout, then hard-redirects to login.
 * Message is stashed in sessionStorage so it survives the reload.
 */
async function forceLogout(
	api: BaseQueryApi,
	message: string,
	extraOptions: Record<string, unknown> = {},
) {
	if (logoutInFlight) return;
	logoutInFlight = true;
	resetRefreshPromise();
	meRefreshPromise = null;

	const authState = (api.getState() as RootState).auth;
	if (authState.accessToken) {
		try {
			await realBaseQuery(
				{ url: API_ENDPOINTS.auth.logout, method: "POST" },
				api,
				extraOptions,
			);
		} catch {
			// Best-effort: still clear local session if logout API fails.
		}
	}

	api.dispatch(logout());
	try {
		sessionStorage.setItem(FORCE_LOGOUT_MESSAGE_KEY, message);
	} catch {
		// ignore storage errors
	}
	if (typeof window !== "undefined") {
		window.location.assign("/login");
	}
}

async function attemptTokenRefresh(
	api: BaseQueryApi,
	extraOptions: Record<string, unknown>,
	refreshToken: string,
) {
	if (!refreshPromise) {
		refreshPromise = (
			realBaseQuery(
				{
					url: API_ENDPOINTS.auth.refresh,
					method: "POST",
					body: { refreshToken },
				},
				api,
				extraOptions,
			) as Promise<{ data?: unknown; error?: FetchBaseQueryError }>
		).finally(() => {
			queueMicrotask(() => {
				refreshPromise = null;
			});
		});
	}

	const refreshResult = await refreshPromise;
	const refreshBody = refreshResult.data as Record<string, unknown> | undefined;
	const refreshData = refreshBody?.data as Record<string, unknown> | undefined;

	if (refreshData?.accessToken) {
		return {
			ok: true as const,
			accessToken: refreshData.accessToken as string,
			refreshToken:
				(refreshData.refreshToken as string | undefined) || refreshToken,
		};
	}

	const refreshError = refreshResult.error;
	const refreshMessage =
		extractApiErrorMessage(refreshError) ??
		"Your session has expired. Please log in again.";

	return {
		ok: false as const,
		message: refreshMessage,
	};
}

/** Soft-refresh module access via GET /me after INSUFFICIENT_PERMISSION. */
async function refreshCurrentUserPermissions(
	api: BaseQueryApi,
	extraOptions: Record<string, unknown>,
): Promise<boolean> {
	if (!meRefreshPromise) {
		meRefreshPromise = (async () => {
			const meResult = await realBaseQuery(
				{ url: API_ENDPOINTS.auth.me, method: "GET" },
				api,
				extraOptions,
			);
			if (meResult.error) return false;
			const user = parseLoginResponse(meResult.data).user;
			if (!user.id) return false;
			const current = (api.getState() as RootState).auth.user;
			api.dispatch(updateUser(mergeSessionUser(current, user)));
			return true;
		})().finally(() => {
			meRefreshPromise = null;
		});
	}
	return meRefreshPromise;
}

const baseQueryWithReauth: BaseQueryFn<
	string | FetchArgs,
	unknown,
	FetchBaseQueryError
> = async (args, api, extraOptions) => {
	const result = await realBaseQuery(args, api, extraOptions);

	if (!result.error) {
		return result;
	}

	const { message: apiMessage, code } = parseApiError(result.error);
	const fallbackMessage =
		apiMessage ?? "You are not authorized to perform this action.";
	const status = result.error.status;
	const authState = (api.getState() as RootState).auth;
	const extras = extraOptions as Record<string, unknown>;

	if (status === 403 && isInsufficientPermissionCode(code)) {
		await refreshCurrentUserPermissions(api, extras);
		if (apiMessage) {
			dispatchAuthToastOnce(api, { type: "error", message: apiMessage });
		}
		return result;
	}

	if (status === 403 && isForceLogoutAuthCode(code)) {
		await forceLogout(
			api,
			apiMessage ?? "Your account is not allowed to sign in.",
			extras,
		);
		return result;
	}

	if (
		!isPublicAuthEndpoint(args) &&
		(status === 401 || isTokenExpiredAuthCode(code))
	) {
		if (isForceLogoutAuthCode(code)) {
			await forceLogout(
				api,
				apiMessage ?? "Your session is no longer valid. Please sign in again.",
				extras,
			);
			return result;
		}

		if (authState.refreshToken) {
			const refreshOutcome = await attemptTokenRefresh(
				api,
				extras,
				authState.refreshToken,
			);

			if (refreshOutcome.ok) {
				if (!authState.user || !authState.authProvider) {
					await forceLogout(
						api,
						"Your session is no longer valid. Please sign in again.",
						extras,
					);
					return result;
				}

				api.dispatch(
					setCredentials({
						accessToken: refreshOutcome.accessToken,
						refreshToken: refreshOutcome.refreshToken,
						user: authState.user,
						authProvider: authState.authProvider,
					}),
				);
				return await realBaseQuery(args, api, extraOptions);
			}

			await forceLogout(api, refreshOutcome.message, extras);
			return result;
		}

		await forceLogout(api, fallbackMessage, extras);
		return result;
	}

	const uploadValidationErrors = extractSpreadsheetUploadValidationErrors(
		result.error,
	);
	if (
		isSpreadsheetUploadValidationFailure(
			code,
			requestUrl(args),
			uploadValidationErrors,
		)
	) {
		const formatted = formatSpreadsheetUploadValidationToast(
			uploadValidationErrors,
		);
		dispatchApiErrorToastOnce(api, {
			type: "error",
			title: formatted.title,
			message: formatted.message,
			lines: formatted.lines,
			duration: 12000,
		});
		return result;
	}

	const backendMessage = extractApiErrorMessage(result.error);
	if (
		backendMessage &&
		!isRefreshEndpoint(args) &&
		!isTokenReauthError(status, code, backendMessage)
	) {
		const timestampConflict = isTimestampOverwriteConflict(status, args);
		dispatchApiErrorToastOnce(api, {
			type: "error",
			title: timestampConflict ? "Refresh required" : undefined,
			message: backendMessage,
			duration: timestampConflict ? 8000 : undefined,
		});
	}

	return result;
};

export const apiSlice = createApi({
	baseQuery: baseQueryWithReauth,
	refetchOnMountOrArgChange: 60,
	refetchOnFocus: false,
	tagTypes: [
		"User",
		"Auth",
		"Property",
		"Events",
		"EventTypes",
		"PropertyMarketing",
		"Role",
		"RoleModule",
		"SubAdmin",
		"FnBRestaurant",
		"BeneficiaryApproval",
		"AppUpdate",
		"StaticContent",
		"PropertyExploreScreen",
		"PropertyHomeScreen",
		"PropertyPaymentConfiguration",
		"PropertyChargesConfiguration",
		"ChargesManagementProperties",
		"ChargesServicePeriods",
		"ChargesServicePeriodUnits",
		"ChargesUnitInvoice",
		"PropertyRevenueConfiguration",
		"RevenueManagementProperties",
		"RevenueServicePeriods",
		"RevenueServicePeriodUnits",
		"RevenueUnitStatement",
		"PropertyMonthlyStatementConfiguration",
		"MonthlyStatementProperties",
		"MonthlyStatementServicePeriods",
		"MonthlyStatementPeriodUnits",
		"MonthlyStatementUnitStatement",
	],
	endpoints: () => ({}),
});
